import React, { useEffect } from 'react';

/**
 * Extract GTM Container ID from GTM code
 * Validates that it's a proper GTM-XXXXXXX format
 */
function extractGtmId(code) {
  if (!code) return null;
  
  // Look for GTM-XXXXXXX pattern
  const gtmIdMatch = code.match(/GTM-[A-Z0-9]+/i);
  if (gtmIdMatch) {
    const gtmId = gtmIdMatch[0].toUpperCase();
    // Validate format: GTM- followed by alphanumeric (typically 7 chars)
    if (/^GTM-[A-Z0-9]{4,10}$/i.test(gtmId)) {
      return gtmId;
    }
  }
  return null;
}

/**
 * Extract Google Analytics (gtag) Measurement ID from gtag code
 * Validates that it's a proper G-XXXXXXXXXX or UA-XXXXXXXXX-X format
 */
function extractGtagId(code) {
  if (!code) return null;
  
  // Look for G-XXXXXXXXXX (GA4) or UA-XXXXXXXXX-X (Universal Analytics) pattern
  const gtagIdMatch = code.match(/(G-[A-Z0-9]+|UA-\d+-\d+)/i);
  if (gtagIdMatch) {
    const gtagId = gtagIdMatch[0].toUpperCase();
    // Validate format: G- followed by alphanumeric (GA4) or UA- format (Universal Analytics)
    if (/^(G-[A-Z0-9]+|UA-\d+-\d+)$/i.test(gtagId)) {
      return gtagId;
    }
  }
  return null;
}

/**
 * Validate that script URLs are from googletagmanager.com or google-analytics.com
 */
function isValidGtmUrl(url) {
  if (!url) return false;
  try {
    const urlObj = new URL(url);
    return urlObj.hostname === 'www.googletagmanager.com' || 
           urlObj.hostname === 'googletagmanager.com' ||
           urlObj.hostname === 'www.google-analytics.com' ||
           urlObj.hostname === 'google-analytics.com';
  } catch {
    return false;
  }
}

/**
 * Safely inject GTM head script
 * Only allows scripts from googletagmanager.com
 */
function injectGtmHead(gtmId) {
  if (!gtmId) return;
  
  // Check if GTM is already loaded
  if (window.dataLayer && document.querySelector(`script[src*="googletagmanager.com/gtm.js?id=${gtmId}"]`)) {
    return;
  }
  
  // Initialize dataLayer
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    'gtm.start': new Date().getTime(),
    event: 'gtm.js'
  });
  
  // Create script element safely (no innerHTML)
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;
  
  // Validate URL before adding
  if (isValidGtmUrl(script.src)) {
    const firstScript = document.getElementsByTagName('script')[0];
    if (firstScript && firstScript.parentNode) {
      firstScript.parentNode.insertBefore(script, firstScript);
    } else {
      document.head.appendChild(script);
    }
  }
}

/**
 * Safely inject GTM noscript iframe
 * Only allows iframes pointing to googletagmanager.com
 */
function injectGtmBody(gtmId) {
  if (!gtmId) return;
  
  // Check if noscript iframe already exists
  if (document.querySelector(`noscript iframe[src*="googletagmanager.com/ns.html?id=${gtmId}"]`)) {
    return;
  }
  
  const iframeUrl = `https://www.googletagmanager.com/ns.html?id=${gtmId}`;
  
  // Validate URL before creating iframe
  if (!isValidGtmUrl(iframeUrl)) {
    return;
  }
  
  // Create noscript and iframe elements safely (no innerHTML)
  const noscript = document.createElement('noscript');
  const iframe = document.createElement('iframe');
  iframe.src = iframeUrl;
  iframe.height = '0';
  iframe.width = '0';
  iframe.style.display = 'none';
  iframe.style.visibility = 'hidden';
  
  noscript.appendChild(iframe);
  
  // Insert at the beginning of body
  if (document.body.firstChild) {
    document.body.insertBefore(noscript, document.body.firstChild);
  } else {
    document.body.appendChild(noscript);
  }
}

/**
 * Safely inject gtag (Google Analytics) code
 * Extracts measurement ID and injects scripts safely
 */
function injectGtag(gtagId) {
  if (!gtagId) return;
  
  // Check if gtag is already loaded
  const existingScript = document.querySelector(`script[src*="googletagmanager.com/gtag/js?id=${gtagId}"]`);
  if (existingScript) {
    return;
  }
  
  // Initialize dataLayer
  window.dataLayer = window.dataLayer || [];
  
  // Create gtag function if it doesn't exist
  if (typeof window.gtag === 'undefined') {
    window.gtag = function() {
      window.dataLayer.push(arguments);
    };
  }
  
  // Create and inject the gtag.js script
  const script1 = document.createElement('script');
  script1.async = true;
  script1.src = `https://www.googletagmanager.com/gtag/js?id=${gtagId}`;
  
  // Validate URL before adding
  if (isValidGtmUrl(script1.src)) {
    // Insert at the very beginning of head
    const firstScript = document.getElementsByTagName('script')[0];
    if (firstScript && firstScript.parentNode) {
      firstScript.parentNode.insertBefore(script1, firstScript);
    } else {
      document.head.insertBefore(script1, document.head.firstChild);
    }
    
    // Create and inject the configuration script
    const script2 = document.createElement('script');
    script2.textContent = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', '${gtagId}');
    `;
    
    // Insert configuration script after gtag.js
    script1.onload = () => {
      if (script1.nextSibling) {
        script1.parentNode.insertBefore(script2, script1.nextSibling);
      } else {
        script1.parentNode.appendChild(script2);
      }
    };
  }
}

/**
 * Safely parse and inject custom script code
 * Only allows scripts from trusted Google domains
 */
function injectCustomScript(code) {
  if (!code) return false;
  
  const trimmed = code.trim();
  if (!trimmed) return false;
  
  // Only process if it contains script tags
  if (!trimmed.includes('<script')) {
    return false;
  }
  
  // Extract all script tags and their attributes
  const scriptRegex = /<script[^>]*>([\s\S]*?)<\/script>/gi;
  const scripts = [];
  let match;
  
  while ((match = scriptRegex.exec(trimmed)) !== null) {
    const fullMatch = match[0];
    const scriptContent = match[1];
    const scriptTag = match[0].substring(0, match[0].indexOf('>') + 1);
    
    // Extract src attribute if present
    const srcMatch = scriptTag.match(/src=["']([^"']+)["']/i);
    const src = srcMatch ? srcMatch[1] : null;
    
    // Only allow scripts from trusted Google domains
    if (src && !isValidGtmUrl(src)) {
      console.warn('Blocked script from untrusted domain:', src);
      continue;
    }
    
    scripts.push({
      src: src,
      content: scriptContent.trim(),
      fullTag: fullMatch
    });
  }
  
  // Inject valid scripts
  scripts.forEach((script, index) => {
    const scriptElement = document.createElement('script');
    
    if (script.src) {
      scriptElement.src = script.src;
      scriptElement.async = true;
    } else if (script.content) {
      // For inline scripts, only allow gtag configuration
      if (script.content.includes('gtag') || script.content.includes('dataLayer')) {
        scriptElement.textContent = script.content;
      } else {
        console.warn('Blocked inline script - only gtag/dataLayer allowed');
        return;
      }
    } else {
      return;
    }
    
    // Insert at the beginning of head
    const firstScript = document.getElementsByTagName('script')[0];
    if (firstScript && firstScript.parentNode) {
      firstScript.parentNode.insertBefore(scriptElement, firstScript);
    } else {
      document.head.insertBefore(scriptElement, document.head.firstChild);
    }
  });
  
  return scripts.length > 0;
}

/**
 * Parse and validate GTM/gtag code from admin settings
 * Supports both GTM and gtag formats
 */
function parseGtmCode(code) {
  if (!code) return { type: null, id: null };
  
  const trimmed = code.trim();
  
  // Try to extract GTM ID first
  const gtmId = extractGtmId(trimmed);
  if (gtmId) {
    return { type: 'gtm', id: gtmId };
  }
  
  // Try to extract gtag ID
  const gtagId = extractGtagId(trimmed);
  if (gtagId) {
    return { type: 'gtag', id: gtagId };
  }
  
  return { type: null, id: null };
}

const GtmInjector = () => {
  useEffect(() => {
    const apiBase = process.env.NODE_ENV === 'development' ? 'http://localhost:8000' : '';
    let canceled = false;
    
    (async () => {
      try {
        const res = await fetch(`${apiBase}/backend/admin_api.php?action=getSiteScripts`);
        const json = await res.json();
        
        if (canceled || json?.status !== 'success') return;
        
        // Extract codes from head and body
        const gtmHeadCode = json.data?.gtm_head?.trim();
        const gtmBodyCode = json.data?.gtm_body?.trim();
        
        // Parse head code
        if (gtmHeadCode) {
          const parsed = parseGtmCode(gtmHeadCode);
          
          if (parsed.type === 'gtm' && parsed.id) {
            // Inject GTM
            injectGtmHead(parsed.id);
          } else if (parsed.type === 'gtag' && parsed.id) {
            // Inject gtag
            injectGtag(parsed.id);
          } else {
            // Try to inject as custom script (with security checks)
            const injected = injectCustomScript(gtmHeadCode);
            if (!injected) {
              console.warn('Could not parse GTM/gtag code from head. Please ensure it contains GTM-XXXXXXX or G-XXXXXXXXXX format.');
            }
          }
        }
        
        // Parse body code (usually for GTM noscript)
        if (gtmBodyCode) {
          const parsed = parseGtmCode(gtmBodyCode);
          
          if (parsed.type === 'gtm' && parsed.id) {
            // Inject GTM noscript iframe
            injectGtmBody(parsed.id);
          } else if (parsed.type === 'gtag') {
            // gtag doesn't need body code, skip
          } else {
            // Try to inject as custom script (with security checks)
            injectCustomScript(gtmBodyCode);
          }
        }
      } catch (e) {
        // Silently fail - GTM is not critical for app functionality
        console.warn('GTM injection failed:', e);
      }
    })();
    
    return () => { canceled = true; };
  }, []);
  
  return null;
};

export default GtmInjector;


